import InboxOutlined from '@mui/icons-material/InboxOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { type FormEvent, useMemo, useState } from 'react';
import type { CategoryRow } from './singleTableListSupport';
import {
  buildDisplayLanguages,
  useBookLanguagesForForm,
} from './bookLanguageFormSupport';
import {
  categoryNameMapFromRow,
  categoryRowSummaryLabel,
  formatSettingsRowDate,
  settingsListApiError,
} from './singleTableListSupport';
import MultilingualNameMapCell from './MultilingualNameMapCell';
import { createCategory, updateCategory, uploadCategoryImageFile } from './categoryApi';
import CategoryListForm, {
  categoryDraftToNamePayload,
  validateCategoryDraft,
  type CategoryDraft,
} from './CategoryListForm';
import SettingsCrudDialog from './SettingsCrudDialog';
import SettingsListDeleteButton from './SettingsListDeleteButton';
import SettingsListEditButton from './SettingsListEditButton';
import SettingsListSection from './SettingsListSection';
import { useCategoryList } from './useCategoryList';
import { s3ObjectPublicUrl } from '../../../utils/s3PublicUrl';

const cardSx = {
  position: 'relative' as const,
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  p: 2,
  pr: 1,
  pb: 5,
  overflow: 'hidden',
  transition: (theme: Theme) =>
    theme.transitions.create(['box-shadow', 'border-color', 'background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
  borderColor: 'divider',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
    boxShadow: (theme: Theme) => theme.shadows[3],
  },
};

export default function CategoryList() {
  const { rows, loading, error, load, deleteById, deletingId } = useCategoryList<CategoryRow>();
  const [draft, setDraft] = useState<CategoryDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const dialogOpen = draft !== null;
  const { rows: langRows, loading: langLoading, error: langError } = useBookLanguagesForForm(dialogOpen);
  const displayLanguages = useMemo(() => buildDisplayLanguages(langRows), [langRows]);

  const openCreate = () => {
    setFormError(null);
    setDraft({ mode: 'create', id: '', name: {}, image_path: null, imageFile: null });
  };

  const openEdit = (row: CategoryRow) => {
    setFormError(null);
    const p = row.image_path?.trim();
    setDraft({
      mode: 'edit',
      id: row.id,
      name: categoryNameMapFromRow(row),
      image_path: p ? p : null,
      imageFile: null,
    });
  };

  const closeForm = () => {
    setDraft(null);
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setFormError(null);
    if (langLoading) {
      setFormError('Хэлийн жагсаалт ачааллаж байна. Түр хүлээнэ үү.');
      return;
    }
    if (langError) {
      setFormError(langError);
      return;
    }
    const validation = validateCategoryDraft(draft, displayLanguages);
    if (validation) {
      setFormError(validation);
      return;
    }
    const name = categoryDraftToNamePayload(draft);
    setSaving(true);
    try {
      if (draft.mode === 'create') {
        const { id } = await createCategory({ name });
        if (draft.imageFile) {
          const image_path = await uploadCategoryImageFile(id, draft.imageFile);
          await updateCategory({ id, image_path });
        }
      } else {
        const id = draft.id.trim();
        const body: Record<string, unknown> = { id, name };
        if (draft.imageFile) {
          body.image_path = await uploadCategoryImageFile(id, draft.imageFile);
        }
        await updateCategory(body);
      }
      closeForm();
      await load({ silent: true });
    } catch (err: unknown) {
      setFormError(
        settingsListApiError(err, draft.mode === 'create' ? 'Нэмэхэд алдаа гарлаа.' : 'Шинэчлэхэд алдаа гарлаа.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle =
    draft?.mode === 'create' ? 'Шинэ категори' : draft?.mode === 'edit' ? 'Категори засах' : '';

  const itemLabelForRow = (row: CategoryRow) =>
    typeof (row.name ?? row.name_map) === 'string'
      ? String(row.name ?? row.name_map)
      : categoryRowSummaryLabel(row);

  return (
    <>
      <SettingsCrudDialog
        open={dialogOpen}
        title={dialogTitle}
        error={formError}
        saving={saving}
        onClose={closeForm}
        onSubmit={(e) => void handleSubmit(e)}
      >
        {draft && (
          <CategoryListForm
            draft={draft}
            onChange={setDraft}
            languages={displayLanguages}
            languagesLoading={langLoading}
            languagesError={langError}
          />
        )}
      </SettingsCrudDialog>

      <SettingsListSection
        title="Категори"
        subtitle="Номын ангилалын жагсаалт"
        onAdd={openCreate}
        error={loading ? null : error}
        loading={loading}
        skeletonColumns={4}
      >
        <Box sx={{ p: 2, pt: 1.5 }}>
          {rows.length === 0 ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <InboxOutlined sx={{ fontSize: 40, color: 'action.disabled' }} aria-hidden />
              <Typography variant="body2" color="text.secondary">
                Мэдээлэл байхгүй
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                  lg: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
                },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              {rows.map((row) => {
                const cardImageUrl = s3ObjectPublicUrl(row.image_path);
                return (
                <Paper
                  key={row.id}
                  variant="outlined"
                  component="article"
                  aria-label={itemLabelForRow(row)}
                  sx={cardSx}
                >
                  {cardImageUrl && (
                    <Box
                      sx={{
                        mx: -2,
                        mt: -2,
                        mb: 1.5,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box
                        component="img"
                        src={cardImageUrl}
                        alt=""
                        sx={{
                          maxHeight: '100%',
                          maxWidth: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      minWidth: 0,
                      pr: 0.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      title={row.id}
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: '1 1 auto',
                      }}
                    >
                      {row.id}
                    </Typography>
                    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                      <SettingsListEditButton onEdit={() => openEdit(row)} />
                    </Box>
                  </Box>

                  <Box sx={{ flex: '1 1 auto', minWidth: 0, pr: 0.5 }}>
                    {typeof (row.name ?? row.name_map) === 'string' ? (
                      <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                        {String(row.name ?? row.name_map ?? '')}
                      </Typography>
                    ) : (
                      <MultilingualNameMapCell flat={categoryNameMapFromRow(row)} layout="grid" />
                    )}
                  </Box>

                  <Box sx={{ mt: 'auto', pt: 2, display: 'flex', flexDirection: 'column', gap: 0.5, pr: 4 }}>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
                      <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Шинэчилсэн:{' '}
                      </Box>
                      {(row.updated_by ?? '').trim() || '—'}
                      <Box component="span" sx={{ color: 'text.disabled' }}> · </Box>
                      {formatSettingsRowDate(row.updated_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
                      <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Үүсгэсэн:{' '}
                      </Box>
                      {(row.created_by ?? '').trim() || '—'}
                      <Box component="span" sx={{ color: 'text.disabled' }}> · </Box>
                      {formatSettingsRowDate(row.created_at)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: 'absolute',
                      right: (theme) => theme.spacing(0.5),
                      bottom: (theme) => theme.spacing(0.5),
                    }}
                  >
                    <SettingsListDeleteButton
                      id={row.id}
                      deletingId={deletingId}
                      onDelete={deleteById}
                      itemLabel={itemLabelForRow(row)}
                    />
                  </Box>
                </Paper>
              );
              })}
            </Box>
          )}
        </Box>
      </SettingsListSection>
    </>
  );
}
